"use client";

import { useState, useEffect } from "react";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Avatar,
  AvatarFallback,
  Input,
} from "@gogive/design-system";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@gogive/design-system/input-group";
import { globalSearch, GlobalSearchResult } from "@/services/account.service";
import { useRouter } from "next/navigation";
import { useDebounce } from "@/hooks/useDebounce";
import { SearchIcon, XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { DONOR_FILTER_TOOLBAR_CONTROL_CLASS } from "@/components/donor-filters/constants";

type GlobalSearchInputProps = {
  /** Atlas default: search lives in the app sidebar. */
  variant?: "sidebar" | "topbar";
};

export function GlobalSearchInput({
  variant = "sidebar",
}: GlobalSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const router = useRouter();
  const isSidebar = variant === "sidebar";

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery.trim()) {
        setResults([]);
        setIsLoading(false);
        setOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await globalSearch(debouncedSearchQuery);
        setResults(response.result);
        if (response.result.length > 0) {
          setOpen(true);
        }
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
        setOpen(false);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchQuery]);

  const handleResultClick = (donorId: number) => {
    setOpen(false);
    setSearchQuery("");
    setResults([]);
    router.push(`/donors/${donorId}`);
  };

  const handleClear = () => {
    setSearchQuery("");
    setResults([]);
    setOpen(false);
  };

  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || "";
    return `${firstInitial}${lastInitial}` || "?";
  };

  const showResults = open && results.length > 0;
  const showClear = searchQuery.length > 0;

  const searchField = isSidebar ? (
    <InputGroup
      className={cn("h-9 w-full", DONOR_FILTER_TOOLBAR_CONTROL_CLASS)}
    >
      <InputGroupAddon align="inline-start">
        <SearchIcon className="size-4 text-muted-foreground" aria-hidden />
      </InputGroupAddon>
      <InputGroupInput
        type="text"
        placeholder="Keyword search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0) {
            setOpen(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        className="h-9 appearance-none [&::-webkit-search-cancel-button]:hidden"
        aria-label="Search donors"
        autoComplete="off"
      />
      {showClear ? (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Clear search"
            onClick={handleClear}
            disabled={isLoading}
          >
            <XIcon className="size-4" />
          </InputGroupButton>
        </InputGroupAddon>
      ) : isLoading ? (
        <InputGroupAddon align="inline-end">
          <div
            className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
            aria-hidden
          />
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  ) : (
    <div className="relative w-full max-w-2xl">
      <SearchIcon
        className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-primary-foreground/70"
        aria-hidden
      />
      <Input
        type="search"
        placeholder="Search donors..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0) {
            setOpen(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        className="h-10 w-full border-primary-foreground/20 bg-primary-foreground/20 pl-9 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:border-primary-foreground/40 focus-visible:bg-primary-foreground focus-visible:text-foreground focus-visible:placeholder:text-foreground/80 focus-visible:ring-0"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div
            className="size-4 animate-spin rounded-full border-2 border-primary-foreground/20 border-t-primary-foreground"
            aria-hidden
          />
        </div>
      )}
    </div>
  );

  return (
    <Popover open={showResults} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn("relative w-full", !isSidebar && "max-w-2xl")}>
          {searchField}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        side={isSidebar ? "right" : "bottom"}
        sideOffset={isSidebar ? 8 : 4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {results.length > 0 ? (
              <CommandGroup heading="Donors">
                {results.map((donor) => (
                  <CommandItem
                    key={donor.id}
                    onSelect={() => handleResultClick(donor.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex w-full min-w-0 items-center gap-3">
                      <Avatar size="sm" style={{ width: 32, height: 32 }}>
                        <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                          {getInitials(donor.firstName, donor.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {donor.firstName} {donor.lastName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {donor.email}
                          {donor.phone && ` • ${donor.phone}`}
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
